'use client';

import { useState, useEffect } from 'react';
import Link from 'next/link';
import Navbar from '../../../../components/Navbar';
import Footer from '../../../../components/Footer';
import useCheckLogin from '../../../../components/CheckLogin';
import ExpertHelpCard from '../../../../components/ExpertHelpCard';
import FilterPanel from '../../../../components/FilterPanel';
import HorizontalFilterBar from '../../../../components/HorizontalFilterBar';
import AdBanner from '../../../../components/AdBanner';
import MobileListingTopBar from '../../../../components/MobileListingTopBar';
import { api } from '@/lib/apiClient';
import { useSearchParams } from 'next/navigation';


const BASE_URL = process.env.NEXT_PUBLIC_API_URL || '/api/proxy/';

const formatDate = (dateStr) => {
    if (!dateStr) return null;
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return null;

    const now = new Date();
    const diffTime = Math.abs(now - date);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays <= 1) {
        return 'Posted today';
    } else if (diffDays <= 30) {
        return `Posted ${diffDays} days ago`;
    } else {
        return `Posted on ${date.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}`;
    }
};

const sortOptions = [
    { value: 'relevance', label: 'Relevance' },
    { value: 'newest', label: 'Newest First' },
    { value: 'rating', label: 'Top Rated' },
    { value: 'experience', label: 'Most Experienced' },
    { value: 'price_low', label: 'Price: Low to High' },
];

function convertToArray(str) {
    if (!str) return [];
    return str.split('-').map(num => parseInt(num, 10));
}

export default function CivilServicesPage() {
    const isLoggedIn = useCheckLogin();
    const [filters, setFilters] = useState({});
    const [sortBy, setSortBy] = useState('relevance');
    const [isMobileFilterOpen, setIsMobileFilterOpen] = useState(false);

    const [mockServices, setMockServices] = useState([]);
    const [filteredServices, setFilteredServices] = useState([]);
    const searchParams = useSearchParams();
    const cityId = searchParams.get("cityId");
    const [city_name, setCityName] = useState("");
    const [localities, setLocalities] = useState("");
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [parms, setParms] = useState("");

    // Read localities from query params if any are present
    const localitiesParam = searchParams.get("localities");
    const initialLocalities = localitiesParam ? localitiesParam.split('-').map(num => parseInt(num, 10)) : [];

    const [localitiesIds, setLocalitiesIds] = useState(initialLocalities);

    useEffect(() => {
        fetchProperties();
    }, [searchParams]);


    const fetchProperties = async () => {
        try {
            console.log("searchParams", searchParams.toString());
            setParms(searchParams.toString());
            const localitiesArray = convertToArray(searchParams.get("localities"));
            setLocalitiesIds(localitiesArray);
            let whereClause = "city_id=$1";
            let params = [cityId];

            const res = await api.post('selectwithout/view_home_cities', { sqlData: "*", where: "id=$1", whereData: [cityId] });
            //console.log("res.data", res.data);
            let city_name = res.data[0].name;
            setCityName(city_name);
            const res1 = await api.post('selectwithout/master_localities', {
                sqlData: "locality_name as name",
                where: "id = ANY($1)",
                whereData: [localitiesArray],
            });
            if (res1.success === 1 && Array.isArray(res1.data)) {
                let localitiesNames = "";
                for (let i = 0; i < res1.data.length; i++) {
                    localitiesNames += res1.data[i].name + ", ";
                }
                setLocalities(localitiesNames);
            }

            if (localitiesArray && localitiesArray.length > 0) {
                whereClause += " and locality_id = ANY($2)";
                params.push(localitiesArray);
            }

            whereClause += " order by created_at desc";

            const response = await api.post('selectwithout/view_admin_civil_suppliers',
                {
                    sqlData: "*, (SELECT supplier_logo FROM suppliers WHERE suppliers.id = view_admin_civil_suppliers.id) AS supplier_logo",
                    where: whereClause,
                    whereData: params,
                });
            console.log("response.data", response.data);
            let service_cards = [];
            for (let i = 0; i < response.data.length; i++) {
                let service_card = {
                    id: response.data[i].id,
                    name: response.data[i].name,
                    price: 0,
                    priceUnit: "",
                    pricePerSqft: "",
                    type: response.data[i].services,
                    experience: response.data[i].experience,
                    rating: response.data[i].rating,
                    address: response.data[i].address,
                    city: response.data[i].city,
                    image: response.data[i].supplier_logo || response.data[i].images,
                    imageCount: response.data[i].imageCount,
                    badges: response.data[i].badges,
                    isNew: response.data[i].isNew,
                    isVerified: response.data[i].isVerified,
                    isSaved: response.data[i].isSaved,
                    listingType: response.data[i].listingType,
                    created_at: response.data[i].created_at || null,
                };
                service_cards.push(service_card);
            }
            setMockServices(service_cards);
        } catch (error) {
            console.error('Error fetching properties:', error);
        }
    };

    useEffect(() => {
        let filtered = [...mockServices];

        // Service Type
        if (filters.serviceType?.length) {
            filtered = filtered.filter(s => {
                let sTypes = [];
                if (typeof s.type === 'string') {
                    sTypes = s.type.split(',').map(x => x.trim().toLowerCase());
                } else if (Array.isArray(s.type)) {
                    sTypes = s.type.map(x => String(x).trim().toLowerCase());
                }
                return filters.serviceType.some(opt => sTypes.includes(opt.toLowerCase()));
            });
        }

        // Experience
        if (filters.experience?.length) {
            filtered = filtered.filter(s => {
                const years = parseInt(s.experience, 10);
                if (isNaN(years)) return false;
                return filters.experience.some(opt => {
                    if (opt === '1-3') return years >= 1 && years <= 3;
                    if (opt === '3-5') return years >= 3 && years <= 5;
                    if (opt === '5+') return years >= 5;
                    return false;
                });
            });
        }

        // Sort services
        if (sortBy === 'rating') {
            filtered.sort((a, b) => (parseFloat(b.rating) || 0) - (parseFloat(a.rating) || 0));
        } else if (sortBy === 'experience') {
            filtered.sort((a, b) => (parseInt(b.experience, 10) || 0) - (parseInt(a.experience, 10) || 0));
        } else if (sortBy === 'price_low') {
            filtered.sort((a, b) => (parseFloat(a.price) || 0) - (parseFloat(b.price) || 0));
        } else if (sortBy === 'newest') {
            filtered.sort((a, b) => {
                const timeA = a.created_at ? new Date(a.created_at).getTime() : 0;
                const timeB = b.created_at ? new Date(b.created_at).getTime() : 0;
                if (timeA && timeB) {
                    return timeB - timeA;
                }
                const idA = parseInt(a.id, 10) || 0;
                const idB = parseInt(b.id, 10) || 0;
                return idB - idA;
            });
        }

        setFilteredServices(filtered);
    }, [filters, mockServices, sortBy]);



    const handleFilterChange = (newFilters) => {
        setFilters(newFilters);
        // API call will go here
        console.log('Filters changed:', newFilters);
    };

    const handleSave = (serviceId) => {
        console.log('Save service:', serviceId);
    };

    const detailPath = `/civil/p/`;

    return (
        <>
            <Navbar />
            <MobileListingTopBar
                filters={filters}
                onFilterChange={handleFilterChange}
                listingType="civil"
                onSearch={(city, locs) => {
                    const params = new URLSearchParams();
                    if (city?.id) params.set('cityId', city.id);
                    if (locs?.length > 0) params.set('localities', locs.map(l => l.id).join('-'));
                    const citySlug = city?.name?.toLowerCase() || 'all';
                    window.location.href = `/civil/${citySlug}?${params.toString()}`;
                }}
            />
            <main className="min-h-screen pb-20 lg:pb-0 mb-8">
                {/* Horizontal Filter Bar - Sticky */}
                <div className="hidden lg:block sticky top-16 z-40 horizontal-filter-bar">
                    <HorizontalFilterBar
                        filters={filters}
                        onChange={handleFilterChange}
                        listingType="civil"
                    />
                </div>

                {/* Page Header */}
                <div className="bg-gray">
                    <div className="container-custom py-4">
                        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mt-5 mb-3">
                            <nav
                                className="flex flex-wrap items-center gap-2 text-sm text-gray-500"
                                aria-label="Breadcrumb"
                            >
                                <span className="text-gray-900 font-medium">
                                    <strong> Available {filteredServices.length} Civil Services </strong> in {city_name}
                                </span>
                            </nav>
                            {/* RIGHT – Relevance Select */}
                            <div className="flex items-center justify-between md:justify-end gap-2 w-full md:w-auto shrink-0">
                                <label htmlFor="sortBy" className="text-sm text-gray-600 whitespace-nowrap">
                                    Sort by:
                                </label>
                                <select
                                    id="sortBy"
                                    value={sortBy}
                                    onChange={(e) => setSortBy(e.target.value)}
                                    className="h-9 w-full md:w-auto text-sm px-2 rounded-md bg-white border border-gray-300 text-gray-700 shadow-sm focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary"
                                >
                                    {sortOptions.map((opt) => (
                                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                                    ))}
                                </select>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Main Content Split Layout */}
                <div className="container-custom py-8">
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">

                        {/* Left: Service List (8 cols) */}
                        <div className="lg:col-span-8 flex flex-col gap-4">

                            {/* Service Cards Grid */}
                            <div className="grid grid-cols-2 md:grid-cols-2 xl:grid-cols-3 gap-3">
                                {filteredServices.map((service) => (
                                    <Link
                                        href={`${detailPath}${service.id}?${parms}`}
                                        key={service.id}
                                        className="group bg-white border border-slate-200 rounded-2xl overflow-hidden hover:shadow-2xl hover:-translate-y-1 transition-all duration-300 flex flex-col h-full"
                                    >
                                        {/* Card Cover Image */}
                                        <div className="relative h-30 w-full bg-slate-100 overflow-hidden shrink-0 border-b border-slate-100">
                                            {service.image ? (
                                                <img
                                                    src={service.image.startsWith('data:') || service.image.startsWith('http') ? service.image : `${BASE_URL}${service.image}`}
                                                    alt={service.name}
                                                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                                                />
                                            ) : (
                                                <img
                                                    src="https://images.unsplash.com/photo-1504307651254-35680f356dfd?w=800&auto=format&fit=crop&q=60"
                                                    alt={service.name}
                                                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                                                />
                                            )}
                                        </div>

                                        {/* Content Section */}
                                        <div className="p-3 flex flex-col flex-grow">
                                            <div className="flex-grow">

                                                <div className="inline-flex items-center px-2 py-0.3 rounded-md text-[11px] font-medium bg-slate-100 text-slate-600 mb-3 capitalize">
                                                    {Array.isArray(service.type)
                                                        ? service.type.join(', ')
                                                        : (service.type || (Array.isArray(service.product_list) ? service.product_list.join(', ') : service.product_list))}
                                                </div>
                                                <div className="flex items-start justify-between gap-2 mb-2">
                                                    <h3 className="text-base font-bold text-slate-800 line-clamp-1 group-hover:text-primary transition-colors leading-tight">
                                                        {service.name}
                                                    </h3>
                                                </div>
                                            </div>
                                            <div className="grid grid-cols-2 gap-2">
                                                <div className="flex flex-col group-hover:bg-violet-50/50 transition-colors">
                                                    <span className="text-xs text-slate-500 font-medium mb-0.5">Experience</span>
                                                    <div className="flex items-center gap-1 font-semibold text-slate-700 text-sm">
                                                        {service.experience}
                                                    </div>
                                                </div>
                                                {service.created_at && (
                                                    <div className="flex flex-col group-hover:bg-violet-50/50 transition-colors">
                                                        <span className="text-xs text-slate-500 font-medium mb-0.5">Posted</span>
                                                        <div className="flex items-center gap-1 text-slate-500 text-xs font-semibold">
                                                            {formatDate(service.created_at)}
                                                        </div>
                                                    </div>
                                                )}
                                            </div>

                                            <div className="pt-2 mt-3 border-t border-slate-100 flex items-center justify-between">
                                                <div className="flex items-center gap-1.5 text-slate-500 text-xs overflow-hidden">
                                                    <svg className="w-3.5 h-3.5 shrink-0 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                                                    </svg>
                                                    <span className="truncate">{service.address}, {service.city}</span>
                                                </div>

                                                {service.rating && (
                                                    <div className="flex items-center gap-1 bg-yellow-50 px-1.5 py-0.5 rounded">
                                                        <span className="text-yellow-700 text-xs font-bold">{service.rating}</span>
                                                        <svg className="w-3 h-3 text-yellow-400 fill-current" viewBox="0 0 20 20">
                                                            <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                                                        </svg>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </Link >
                                ))}
                            </div>

                            {/* Load More */}
                            {/* <div className="mt-8 text-center">
                                <button className="btn btn-white border border-gray-200 hover:border-primary hover:text-primary px-8 w-full sm:w-auto">
                                    Load More Services
                                </button>
                            </div> */}
                        </div>

                        {/* Right: Ad Sidebar (4 cols) */}
                        <aside className="lg:col-span-4 hidden lg:flex flex-col gap-3 sticky top-24">
                            <ExpertHelpCard
                                title="Construction Buying Guide"
                                description="Planning to build a home? Get expert advice on materials, timeline, and costs to ensure a smooth construction process. First Register Buyer/Tenant... then Construction Buying Guide..."
                                buttonText="Become a Supplier"
                                link={isLoggedIn ? "/become-civil-suppliers" : "/register"}
                                bgClass="bg-gradient-to-br from-orange-50 to-amber-50/50"
                                borderClass="border-slate-100"
                                titleColorClass="text-orange-900"
                                descColorClass="text-orange-900"
                                btnTextColorClass="text-orange-900 group-hover:text-orange-800"
                                glowColor1="#b95f10ff"
                                glowColor2="#d4852dff"
                            />

                            {/* Ad Banners */}
                            <AdBanner
                                type="services"
                                noofads={2}
                            />
                        </aside>
                    </div>
                </div>

                {/* Mobile Filter Modal */}
                {isMobileFilterOpen && (
                    <div className="fixed inset-0 z-50 lg:hidden">
                        <div
                            className="absolute inset-0 bg-black/50"
                            onClick={() => setIsMobileFilterOpen(false)}
                        />
                        <div className="absolute right-0 top-0 bottom-0 w-full max-w-sm bg-white overflow-y-auto">
                            <FilterPanel
                                filters={filters}
                                onChange={handleFilterChange}
                                listingType="civil"
                                isMobile={true}
                                onClose={() => setIsMobileFilterOpen(false)}
                            />
                        </div>
                    </div>
                )}
            </main>
            <Footer />
        </>
    );
}
